import { Box, Divider } from '@mui/material';
import { Identifier } from '@okletsplay/core';
import { MatchPlayer } from 'app/interfaces/entity';
import { dispatchUnwrap } from 'app/utils/store';
import { gamesSelectorFactory } from 'modules/games/store/games/selectors';
import { MatchPlayerHeader } from 'modules/matches/components/MatchModal/MatchPlayerHeader';
import { acceptJoin, declineJoin } from 'modules/matches/store/current-match/repository';
import { PlayerTile } from 'modules/users/components/PlayerTile/PlayerTile';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { useDefaultUserMenuActions, usePlatformLinkAction } from 'shared/hooks';
import { useStyles } from './JoinRequests.styles';

interface JoinRequestsProps {
  players: MatchPlayer[];
  matchId: Identifier;
  gameId?: Identifier;
}

export const JoinRequests: FC<JoinRequestsProps> = ({ players = [], gameId = '', matchId }) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const { privateMessageActions } = useDefaultUserMenuActions();

  const game = useSelector(gamesSelectorFactory.useById(gameId));
  const { openPlatformLink, isPlatformLinked } = usePlatformLinkAction(game?.platformType);

  const onAcceptHandler = useCallback(
    (userId: Identifier) => {
      if (!isPlatformLinked) {
        openPlatformLink();
        return;
      }

      dispatchUnwrap(acceptJoin({ matchId, userId }));
    },
    [isPlatformLinked, matchId, openPlatformLink]
  );

  const onRejectHandler = useCallback(
    (userId: Identifier) => {
      dispatch(declineJoin({ matchId, userId }));
    },
    [dispatch, matchId]
  );

  if (!players.length) {
    return (
      <Box className={classes.emptyListContainer}>
        <EmptyPlaceholder text="No active join requests." />
      </Box>
    );
  }

  return (
    <>
      {players.map((player, index) => (
        <Box key={player.user.id}>
          <MatchPlayerHeader
            index={index}
            userId={player.user?.userId}
            onAccept={onAcceptHandler}
            onReject={onRejectHandler}
          />
          <Box className={classes.player}>
            <PlayerTile user={player.user} actions={privateMessageActions} />
          </Box>
          {index !== players.length - 1 && <Divider className={classes.divider} />}
        </Box>
      ))}
    </>
  );
};
